"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { cartItemCount } from "@/lib/cart";
import { isWholesalePath } from "@/lib/site-context";

const retailNavigation = [
  { href: "/catalog", label: "Каталог" },
  { href: "/#cities", label: "Города" },
  { href: "/catalog?q=пряник", label: "Пряники" },
  { href: "/catalog?q=подарочный%20набор", label: "Подарочные наборы" },
  { href: "/opt", label: "Оптовикам" },
];

const wholesaleNavigation = [
  { href: "/opt", label: "Оптовый раздел" },
  { href: "/opt/catalog", label: "Оптовый каталог" },
];

function readCartCount() {
  try {
    const value = JSON.parse(localStorage.getItem("cart") || "[]") as { qty?: unknown }[];
    return cartItemCount(value);
  } catch {
    return 0;
  }
}

export function Header() {
  const pathname = usePathname();
  const wholesale = isWholesalePath(pathname);
  const navigation = wholesale ? wholesaleNavigation : retailNavigation;
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    if (wholesale) return;
    const updateCart = () => setCartCount(readCartCount());
    updateCart();
    window.addEventListener("cart-updated", updateCart);
    window.addEventListener("storage", updateCart);
    return () => {
      window.removeEventListener("cart-updated", updateCart);
      window.removeEventListener("storage", updateCart);
    };
  }, [wholesale]);

  return (
    <header className="site-header">
      <div className="site-container flex h-[4.75rem] items-center justify-between gap-5">
        <Link href={wholesale ? "/opt" : "/"} className="group flex min-w-0 items-center gap-3" aria-label={wholesale ? "Golden Gifts для бизнеса — на главную оптового раздела" : "Golden Gifts — на главную"}>
          <span className="grid h-10 w-10 shrink-0 place-items-center border border-[var(--gold)] text-[0.72rem] font-black tracking-[0.16em] text-[var(--gold-light)] transition group-hover:bg-[var(--gold)] group-hover:text-[var(--ink)]">
            GG
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-black tracking-[0.24em] text-white sm:text-base">GOLDEN GIFTS</span>
            <span className="hidden text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-stone-400 sm:block">подарки с характером городов</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-5 xl:flex" aria-label="Основная навигация">
          {navigation.map((item) => (
            <Link key={item.href} href={item.href} className="nav-link">
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1 sm:gap-2">
          {wholesale ? (
            <span className="xl:hidden">
              <Link href="/opt/catalog" className="header-action" aria-label="Оптовый каталог">
                <span className="sm:hidden">Опт</span>
                <span className="hidden sm:inline">Оптовый каталог</span>
              </Link>
            </span>
          ) : (
            <>
              <Link href="/catalog#catalog-start" className="header-action" aria-label="Поиск по каталогу" title="Поиск по каталогу">
                <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-none stroke-current" strokeWidth="1.8">
                  <circle cx="11" cy="11" r="6.5" />
                  <path d="m16 16 4 4" />
                </svg>
                <span className="hidden lg:inline">Поиск</span>
              </Link>
              <span className="header-action hidden cursor-not-allowed text-stone-500 lg:inline-flex" aria-disabled="true" title="Личный кабинет появится позже">
                Войти
              </span>
              <Link href="/checkout" className="header-action relative" aria-label={`Корзина, товаров: ${cartCount}`}>
                <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 fill-none stroke-current" strokeWidth="1.8">
                  <path d="M3 4h2l1.7 10.1a2 2 0 0 0 2 1.7h7.7a2 2 0 0 0 1.9-1.4L21 7H6" />
                  <circle cx="9" cy="20" r="1" />
                  <circle cx="18" cy="20" r="1" />
                </svg>
                <span className="hidden sm:inline">Корзина</span>
                {cartCount > 0 && <span className="cart-count">{cartCount > 99 ? "99+" : cartCount}</span>}
              </Link>
            </>
          )}
          <span className="xl:hidden">
            <button
              type="button"
              className="header-action"
              aria-expanded={menuOpen}
              aria-controls="mobile-navigation"
              aria-label={menuOpen ? "Закрыть меню" : "Открыть меню"}
              onClick={() => setMenuOpen((value) => !value)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className="h-6 w-6 fill-none stroke-current" strokeWidth="1.8">
                {menuOpen ? <path d="M5 5l14 14M19 5 5 19" /> : <path d="M3 6h18M3 12h18M3 18h18" />}
              </svg>
            </button>
          </span>
        </div>
      </div>

      {menuOpen && (
        <div id="mobile-navigation" className="border-t border-white/10 bg-[#0d0906] xl:hidden">
          <nav className="site-container grid gap-1 py-4" aria-label="Мобильная навигация">
            {navigation.map((item) => (
              <Link key={item.href} href={item.href} onClick={() => setMenuOpen(false)} className="mobile-nav-link">
                {item.label}<span aria-hidden="true">→</span>
              </Link>
            ))}
            {wholesale ? (
              <Link href="/opt/catalog" onClick={() => setMenuOpen(false)} className="secondary-button mt-3 justify-center border-t border-white/10">Открыть оптовый каталог</Link>
            ) : (
              <div className="mt-3 grid grid-cols-2 gap-2 border-t border-white/10 pt-4">
                <Link href="/catalog#catalog-start" onClick={() => setMenuOpen(false)} className="secondary-button justify-center">Поиск</Link>
                <span className="secondary-button cursor-not-allowed justify-center text-stone-500" aria-disabled="true">Войти позже</span>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
